/**
 * Inspector fields for Audio pads (2026-10-13). Three zones: the grid and the pad being edited, where the pads play
 * (computer, output with its latency and Measure, that computer's pads level and mute), and the chosen pad (its file
 * from the audio library grouped by type, or a file imported here, and how it plays).
 *
 * A pad setting left alone follows its file's TYPE (`padDefaults`): the pills show the value that will be used and
 * mark it "type default" until the operator picks one.
 */
import { createEffect, createMemo, createSignal, For, on, Show } from 'solid-js';
import { editItem, resizeAudioPads, type AudioPad, type AudioPadsItem, type CellColor } from '../layout/store.ts';
import type { AudioOutput } from '../opbridge/httpClient.ts';
import { anyConnectedReactive, bridges, connectionsVersion, getConnection, primaryBridge } from '../opbridge/store.ts';
import {
  kindOfPad, measureLatency, padDefaults, padsUnavailable, pressPad, readPadsSettings, saveLatency, stopAllPads,
  stopPad, trackOf, writePadsSettings,
} from '../audio/pads.ts';
import { beatmaps, enqueueAudio, outputsOf, playablePcs, refreshTracks, tracks } from '../timeline/testTrackStore.ts';
import { KIND_LABEL, KIND_PLURAL, KINDS, kindOf } from '../timeline/audioKind.ts';
import { pushToast } from '../ui/toast.ts';
import { Tip, TipRow } from './Tip.tsx';

const COLORS: CellColor[] = ['cyan', 'magenta', 'amber', 'green', 'violet', 'yellow', 'pink', 'sky', 'tan', 'red'];
const COLOR_TOKEN: Record<CellColor, string> = {
  cyan: 'idle', magenta: 'incoming', amber: 'armed', green: 'ok', violet: 'violet',
  yellow: 'yellow', pink: 'pink', sky: 'sky', tan: 'tan', red: 'error',
};
const MODES = [
  { id: 'oneshot', label: 'Once', title: 'Plays to the end; pressing again starts it over' },
  { id: 'loop', label: 'Loop', title: 'Loops until you press it again' },
  { id: 'gate', label: 'Hold', title: 'Plays while you hold it' },
] as const;
const QUANTS = [
  { id: 'now', label: 'Now', title: 'Starts the moment you press' },
  { id: 'beat', label: 'Beat', title: "Starts on Ableton's next beat" },
  { id: 'bar', label: 'Bar', title: "Starts on Ableton's next bar" },
] as const;
const WARPS = [
  { id: 'off', label: 'As recorded', title: 'Plays at the speed it was recorded' },
  { id: 'repitch', label: 'To tempo', title: "Sped up or slowed to Ableton's tempo (the pitch moves with it)" },
] as const;

/** Which pad each item's Inspector is editing (kept across re-selection of the item). */
const [chosen, setChosen] = createSignal<Record<string, number>>({});

export function AudioPadsFields(props: { item: AudioPadsItem }) {
  const index = () => Math.min(chosen()[props.item.id] ?? 0, props.item.pads.length - 1);
  const pad = (): AudioPad => props.item.pads[index()] ?? {};
  const setPad = (patch: { [K in keyof AudioPad]?: AudioPad[K] | undefined }) => {
    const pads = props.item.pads.slice();
    const merged: Record<string, unknown> = { ...pads[index()], ...patch };
    for (const k of Object.keys(merged)) if (merged[k] === undefined) delete merged[k];
    pads[index()] = merged as AudioPad;
    editItem(props.item.id, { pads });
  };
  if (tracks().length === 0) void refreshTracks();

  // ---- where it plays ----
  const pcId = () => props.item.pc ?? primaryBridge()?.nodeId ?? '';
  const [outputs, setOutputs] = createSignal<AudioOutput[]>([]);
  createEffect(on(pcId, async (pc) => setOutputs(pc ? await outputsOf(pc) : [])));
  const output = createMemo(() => outputs().find((o) => o.id === props.item.output || o.name === props.item.output)
    ?? outputs().find((o) => o.default) ?? outputs()[0]);
  // OPBridge measures by loopback, which only Windows has: on a Mac it answers 409, so there the latency is typed
  // (OPBridge S318, 2026-10-14). An unknown platform keeps the button: the refusal then says why.
  const onMac = () => {
    void connectionsVersion();
    const b = bridges().find((x) => x.nodeId === pcId());
    return (b ? getConnection(b.id)?.health()?.platform : undefined) === 'macos';
  };
  const [measuring, setMeasuring] = createSignal(false);
  async function measure() {
    const o = output();
    if (!o) return;
    setMeasuring(true);
    const ms = await measureLatency(o.id, props.item.pc);
    setMeasuring(false);
    if (ms !== null) { setOutputs((l) => l.map((x) => (x.id === o.id ? { ...x, latencyMs: ms } : x))); pushToast('ok', `${o.name}: ${Math.round(ms)} ms late`, 2000); }
  }
  async function setLatency(ms: number) {
    const o = output();
    if (!o || !Number.isFinite(ms)) return;
    if (await saveLatency(o.id, ms, props.item.pc)) setOutputs((l) => l.map((x) => (x.id === o.id ? { ...x, latencyMs: ms } : x)));
  }

  const [settings, setSettings] = createSignal<{ level: number; muted: boolean } | null>(null);
  createEffect(on(() => props.item.pc, async (pc) => setSettings(await readPadsSettings(pc))));
  async function putSettings(next: { level?: number; muted?: boolean }) {
    const cur = settings();
    if (cur) setSettings({ ...cur, ...next });
    if (!(await writePadsSettings(props.item.pc, next))) setSettings(await readPadsSettings(props.item.pc));
  }

  // ---- the chosen pad ----
  const kind = () => kindOfPad(pad());
  const defaults = () => padDefaults(kind() ?? 'oneshot');
  let fileInput: HTMLInputElement | undefined;
  async function importFile(files: FileList | null) {
    const f = files?.[0];
    if (!f) return;
    const [t] = await enqueueAudio([f]);
    if (t) setPad({ trackId: t.id });
  }
  const grouped = createMemo(() => KINDS.map((k) => ({
    kind: k, list: tracks().filter((t) => kindOf(t, beatmaps()[t.id]) === k).sort((a, b) => a.name.localeCompare(b.name)),
  })));

  return (
    <>
      <div class="inspector-zone" data-testid="aud-insp-grid">
        <label class="inspector-field">
          <span>Rows</span>
          <input type="number" min="1" max="8" value={props.item.rows}
            onInput={(e) => resizeAudioPads(props.item.id, Math.max(1, Math.min(8, parseInt(e.currentTarget.value, 10) || 1)), props.item.cols)} />
        </label>
        <label class="inspector-field">
          <span>Columns</span>
          <input type="number" min="1" max="8" value={props.item.cols}
            onInput={(e) => resizeAudioPads(props.item.id, props.item.rows, Math.max(1, Math.min(8, parseInt(e.currentTarget.value, 10) || 1)))} />
        </label>
        <Show when={props.item.pads.length > 1}>
          <div class="aud-insp-pick" style={{ '--cols': String(props.item.cols) }} data-testid="aud-insp-pick">
            <For each={props.item.pads}>
              {(p, i) => (
                <button type="button" class="aud-insp-cell" data-selected={i() === index() ? 'true' : 'false'}
                  data-color={COLOR_TOKEN[p.color ?? 'cyan']} title={p.label ?? trackOf(p)?.name ?? 'Empty'}
                  onClick={() => setChosen((o) => ({ ...o, [props.item.id]: i() }))}>
                  {p.label ?? trackOf(p)?.name ?? String(i() + 1)}
                </button>
              )}
            </For>
          </div>
        </Show>
        {/* With nothing connected the notice under the head says so; this
            line is for an OPBridge that is up but cannot play pads. */}
        <Show when={anyConnectedReactive() && padsUnavailable()}>
          {(why) => <span class="inspector-hint">{why() === 'Update OPBridge' ? 'This OPBridge cannot play pads yet: update it.' : `${why()}.`}</span>}
        </Show>
      </div>

      <div class="inspector-zone" data-testid="aud-insp-pad">
        <label class="inspector-field">
          <span>File</span>
          <select data-testid="aud-insp-file" value={pad().trackId ?? ''}
            onChange={(e) => setPad({ trackId: e.currentTarget.value || undefined, mode: undefined, quant: undefined, warp: undefined })}>
            <option value="">None</option>
            <For each={grouped()}>
              {(g) => (
                <Show when={g.list.length > 0}>
                  <optgroup label={KIND_PLURAL[g.kind]}>
                    <For each={g.list}>{(t) => <option value={t.id}>{t.name}</option>}</For>
                  </optgroup>
                </Show>
              )}
            </For>
            <Show when={pad().trackId && !trackOf(pad())}><option value={pad().trackId}>Missing file</option></Show>
          </select>
        </label>
        <div class="inspector-pills">
          <button type="button" class="inspector-pill" onClick={() => fileInput?.click()} title="Import an audio file into the library and put it on this pad">Import...</button>
          <button type="button" class="inspector-pill" disabled={!pad().trackId} data-testid="aud-insp-preview"
            onClick={() => void pressPad(props.item, index())}>Play</button>
          <button type="button" class="inspector-pill" disabled={!pad().trackId}
            onClick={() => void stopPad(props.item, index())}>Stop</button>
          <input ref={fileInput} type="file" accept="audio/*,.mp3,.wav,.flac,.ogg,.aif,.aiff,.m4a" hidden
            onChange={(e) => { void importFile(e.currentTarget.files); e.currentTarget.value = ''; }} />
        </div>
        <Show when={kind()}>{(k) => <TipRow label={`Type: ${KIND_LABEL[k()]}`} text="Settings you leave alone follow its type." />}</Show>
        <label class="inspector-field">
          <span>Pad name</span>
          <input type="text" value={pad().label ?? ''} placeholder={trackOf(pad())?.name ?? 'Pad name'}
            onChange={(e) => setPad({ label: e.currentTarget.value.trim() || undefined })} />
        </label>
        <PillRow label="Plays" testid="aud-insp-mode" options={MODES} value={pad().mode} fallback={defaults().mode} set={(v) => setPad({ mode: v })} />
        <PillRow label="Starts" testid="aud-insp-quant" options={QUANTS} value={pad().quant} fallback={defaults().quant} set={(v) => setPad({ quant: v })} />
        <PillRow label="Speed" testid="aud-insp-warp" options={WARPS} value={pad().warp} fallback={defaults().warp} set={(v) => setPad({ warp: v })} />
        <label class="inspector-field">
          <span>Volume</span>
          <input type="range" min="0" max="2" step="0.05" value={pad().gain ?? 1}
            onInput={(e) => setPad({ gain: Number(e.currentTarget.value) })} />
          <span class="inspector-hint">{Math.round((pad().gain ?? 1) * 100)}%</span>
        </label>
        {/* Labelled (2026-10-15): it was a row of ten swatches with no name. */}
        <div class="inspector-pills inspector-pad-colour">
        <span class="inspector-pills-label">Pad colour</span>
        <div class="bg-cell-row-line bg-cell-row-colors">
          <For each={COLORS}>
            {(c) => (
              <button type="button" class="bg-color-swatch" data-color={COLOR_TOKEN[c]} aria-label={c} title={c}
                data-selected={(pad().color ?? 'cyan') === c ? 'true' : 'false'} onClick={() => setPad({ color: c })} />
            )}
          </For>
        </div>
        </div>
      </div>

      <div class="inspector-zone" data-testid="aud-insp-where">
        <label class="inspector-field">
          <span>Computer</span>
          <select value={props.item.pc ?? ''} onChange={(e) => editItem(props.item.id, { pc: e.currentTarget.value || undefined, output: undefined })}>
            <option value="">{primaryBridge()?.name ?? 'This OPBridge'}</option>
            <For each={playablePcs().filter((p) => p.nodeId !== primaryBridge()?.nodeId)}>{(p) => <option value={p.nodeId}>{p.name}</option>}</For>
          </select>
        </label>
        <label class="inspector-field">
          <span>Output</span>
          <select value={props.item.output ?? ''} onChange={(e) => editItem(props.item.id, { output: e.currentTarget.value || undefined })}>
            <option value="">Default{outputs().find((o) => o.default) ? ` (${outputs().find((o) => o.default)!.name})` : ''}</option>
            <For each={outputs()}>{(o) => <option value={o.id}>{o.name}</option>}</For>
          </select>
        </label>
        <Show when={output()}>
          <label class="inspector-field">
            <span>Latency</span>
            <input type="number" min="0" max="1000" step="1" value={Math.round(output()?.latencyMs ?? 0)}
              title="How late this output really is, in ms. Pads and test tracks start early by this much."
              onChange={(e) => void setLatency(Number(e.currentTarget.value))} />
            <Show when={!onMac()}>
              <button type="button" class="inspector-pill" disabled={measuring()} onClick={() => void measure()}
                title="Plays three quiet clicks and listens for them">{measuring() ? 'Measuring...' : 'Measure'}</button>
            </Show>
          </label>
        </Show>
        <Show when={settings()}>
          {(s) => (
            <label class="inspector-field">
              <span>Pads level</span>
              <input type="range" min="0" max="1" step="0.05" value={s().level} title="A ceiling for every pad on this computer"
                onChange={(e) => void putSettings({ level: Number(e.currentTarget.value) })} />
              <button type="button" class="inspector-pill" data-selected={s().muted ? 'true' : 'false'}
                onClick={() => void putSettings({ muted: !s().muted })}>{s().muted ? 'Muted' : 'Mute'}</button>
            </label>
          )}
        </Show>
        <div class="inspector-pills">
          <button type="button" class="inspector-pill" onClick={() => void stopAllPads()} title="Stops every pad on every computer">Stop all pads</button>
        </div>
      </div>
    </>
  );
}

function PillRow<T extends string>(props: {
  label: string; testid: string; options: ReadonlyArray<{ id: T; label: string; title: string }>;
  value: T | undefined; fallback: T; set: (v: T | undefined) => void;
  /** Wrapped chips for a row whose words do not fit one iPad row. */
  wrap?: boolean;
}) {
  return (
    <div class="inspector-pills" data-testid={props.testid} data-wrap={props.wrap ? 'true' : undefined}>
      <span class="inspector-pills-label">{props.label}</span>
      <For each={props.options}>
        {(o) => (
          <button type="button" class="inspector-pill" title={o.title} data-value={o.id}
            data-selected={(props.value ?? props.fallback) === o.id ? 'true' : 'false'}
            data-default={props.value === undefined && props.fallback === o.id ? 'true' : 'false'}
            onClick={() => props.set(props.value === o.id ? undefined : o.id)}>
            {o.label}
          </button>
        )}
      </For>
    </div>
  );
}
